import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslatePipe} from '@ngx-translate/core';
import {UserSelectionComponent} from '../../components/user-selection/user-selection.component';
import {MatError, MatFormField, MatLabel, MatPrefix, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatOption} from '@angular/material/select';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {AsyncPipe} from '@angular/common';
import {SessionService} from '../../services/session.service';
import {Router} from '@angular/router';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {UserHal} from '../../models/HAL/user-hal.model';
import {CaseDtoModel} from '../../models/DTO/CaseDto.model';
import {CaseHal} from '../../models/HAL/case-hal.model';
import {HttpClient} from '@angular/common/http';
import {SnackBarService} from '../../services/snackbar.service';
import {DialogService} from '../../services/dialog.service';
import {DynamicConfirmationDialogComponent} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {debounceTime, distinctUntilChanged, map, Observable, of, shareReplay, startWith, Subscription, switchMap} from 'rxjs';
import {FormCollectionHal, FormHal} from '../../models/HAL/form-hal.model';
import {SafePipe} from '../../pipes/safe.pipe';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {PostMessageSubscribeTypeEnum} from '../../enums/post-message-subscribe-type.enum';
import {PostMessageSubscriberService} from '../../services/post-message-subscriber.service';
import {PostMessageService} from '../../services/post-message.service';
import {DocumentHal} from '../../models/HAL/document-hal.model';

@Component({
  selector: 'app-case-creation',
  imports: [
    MatIconButton,
    MatIcon,
    TranslatePipe,
    MatButton,
    UserSelectionComponent,
    MatFormField,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatLabel,
    MatOption,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    AsyncPipe,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatPrefix,
    MatSuffix,
    SafePipe,
    MatProgressSpinner
  ],
  templateUrl: './case-creation.component.html',
  styleUrl: './case-creation.component.scss'
})
export class CaseCreationComponent implements OnInit, OnDestroy {
  protected caseContent: string = 'empty';
  protected customer: UserHal | null = null;
  protected chosenBuilderForm: FormHal | null = null;
  protected caseNameFormControl: FormControl = new FormControl('', [Validators.required, Validators.maxLength(64)]);
  protected caseDescriptionFormControl: FormControl = new FormControl('', [Validators.maxLength(64)]);
  protected caseReferenceFormControl: FormControl = new FormControl('', [Validators.maxLength(64)]);
  protected caseTemplateFormControl: FormControl = new FormControl('');
  protected filteredTemplates: Observable<FormHal[]> = of([]);
  protected builderFormUrl: string | undefined = undefined;
  protected loadingSearchResult: boolean = false;
  private iframeLoadedCounter: number = 0;
  private completingDataCapturing: boolean = false;
  private createdCase: CaseHal | null = null;
  private filteredTemplatesSubscription: Subscription | null = null;

  constructor(
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly httpClient: HttpClient,
    private readonly snackBarService: SnackBarService,
    private readonly dialogService: DialogService,
    private readonly postMessageSubscriberService: PostMessageSubscriberService,
    private readonly postMessageService: PostMessageService,
  ) {
    this.loadingSearchResult = true;
    this.subscribeToDataCapturing();
  }

  ngOnInit(): void {
    this.loadingSpinnerService.hide();
    this.caseTemplateFormControl.valueChanges.pipe(
      debounceTime(250)
    ).subscribe(() => {
      this.loadingSearchResult = true;
    });
    this.filteredTemplates = this.caseTemplateFormControl.valueChanges.pipe(
      startWith(''),
      map((value) => value ?? ''),
      distinctUntilChanged(),
      debounceTime(250),
      switchMap((searchParameter) =>
        this.executeFullTextSearch(searchParameter).pipe(
          map((response: FormCollectionHal) => {
            return response._embedded?.forms || [];
          })
        )
      ),
      shareReplay(1)
    );
    this.filteredTemplatesSubscription = this.filteredTemplates.subscribe(() => {
      this.loadingSearchResult = false;
    });
  }

  ngOnDestroy() {
    this.postMessageSubscriberService.unsubscribe(PostMessageSubscribeTypeEnum.DATA_CAPTURING);
    this.filteredTemplatesSubscription?.unsubscribe();
  }

  private executeFullTextSearch(searchParameter: string) {
    let searchBuildFormsUrl = new URL(this.sessionService.getSession().currentTeam?._links.getLink('searchBuilderForms')?.href!);
    searchBuildFormsUrl.searchParams.delete('searchParameter');
    return this.httpClient.get<FormCollectionHal>(searchBuildFormsUrl.href, {
      params: {
        searchParameter: searchParameter
      }
    });
  }

  protected templateSelected(form: FormHal) {
    this.chosenBuilderForm = form;
    this.caseTemplateFormControl.setValue(form.title);
  }

  protected removeChosenTemplate() {
    this.chosenBuilderForm = null;
    this.caseTemplateFormControl.setValue('');
  }

  protected backToCasesOverview(): void {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseCreation.backToCasesOverviewModal.title',
        message: 'caseCreation.backToCasesOverviewModal.message',
        confirmButtonText: 'caseCreation.backToCasesOverviewModal.confirm',
        cancelButtonText: 'caseCreation.backToCasesOverviewModal.cancel'
      }
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.router.navigate([this.sessionService.getSession().currentTeam?._links.getLink('caseOverview')?.getRoute()]).then();
      }
    });
  }

  protected customerSelected(userHal: UserHal) {
    this.customer = userHal;
  }

  protected customerUnselected() {
    this.customer = null;
  }

  protected createCase() {
    this.loadingSpinnerService.show();
    const caseDto: CaseDtoModel = new CaseDtoModel({
      name: this.caseNameFormControl.value,
      description: this.caseDescriptionFormControl.value,
      reference: this.caseReferenceFormControl.value,
      clientUserId: this.customer?.identification,
      creatorUserId: this.sessionService.getSession().user?.identification,
      released: this.chosenBuilderForm == null,
    });

    this.httpClient.post<CaseHal>(this.sessionService.getSession().currentTeam!._links.getLink("createCase")!.href, caseDto).subscribe({
      next: (createdCase: CaseHal) => {
        if (caseDto.released && this.isUnassignedCase(createdCase)) {
          this.incrementUnassignedCaseCount();
        }
        if (this.chosenBuilderForm) {
          this.createdCase = createdCase;
          this.httpClient.get<{
            redirectUrl: string
          }>(new FormHal(this.chosenBuilderForm)?._links.getLink('redirectUrl')?.href!).subscribe({
            next: (response: { redirectUrl: string }) => {
              const url = new URL(response.redirectUrl);
              url.searchParams.set('caseId', createdCase.identification);
              this.builderFormUrl = url.href;
            }
          });
        } else {
          this.snackBarService.open('caseCreation.success', 3);
          this.router.navigate([new CaseHal(createdCase)._links.getLink('self')?.getRoute()]).then();
        }
      },
      error: () => {
        this.snackBarService.open('caseCreation.error', 3, 'error');
      }
    });
  }

  private incrementUnassignedCaseCount() {
    this.sessionService.incrementUnassignedCaseCount(1);
  }

  private isUnassignedCase(caseHal: CaseHal): boolean {
    return caseHal.state?.toUpperCase() === 'UNASSIGNED';
  }

  public completeDataCapturing() {
    this.completingDataCapturing = true;
    this.loadingSpinnerService.show();
    const builderDocumentHal = new DocumentHal({
      'caseId': this.createdCase?.identification,
      'identification': null
    });
    const postMessage = this.postMessageService.getDataCapturingPostMessageModel(builderDocumentHal);
    this.postMessageService.postMessage(document.querySelector("#formBuilder") as HTMLIFrameElement, postMessage);
  }

  protected iframeLoaded() {
    this.iframeLoadedCounter++;
    if (this.iframeLoadedCounter == 1) {
      setTimeout(() => {
        if (this.iframeLoadedCounter == 1) {
          this.loadingSpinnerService.hide();
          this.snackBarService.open('caseCreation.iframeError', 3, 'error');
          this.router.navigate([this.sessionService.getSession().currentTeam?._links.getLink('caseOverview')?.getRoute()]).then();
        }
      }, 10000);
    }
    if (this.iframeLoadedCounter > 1 && !this.completingDataCapturing) {
      this.loadingSpinnerService.hide();
    }
  }

  private subscribeToDataCapturing() {
    this.postMessageSubscriberService.subscribe(PostMessageSubscribeTypeEnum.DATA_CAPTURING, (data) => {
      if (data?.state === "COMPLETE") {
        this.router.navigate([new CaseHal(this.createdCase)?._links.getLink('self')?.getRoute()]).then();
        this.snackBarService.open("caseCreation.caseContents.template.apply.success", 3);
      }
      if (data?.state === "NOT_COMPLETE") {
        this.completingDataCapturing = false;
        this.loadingSpinnerService.hide();
        this.snackBarService.open("caseCreation.caseContents.template.apply.error", 3, "error");
      }
    });
  }
}
